import React, { useRef, useEffect, useState, useContext, createContext, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { cn } from '../../../lib/utils';

// --- Context ---
const MapContext = createContext({
  map: null,
  isLoaded: false
});

export const useMap = () => useContext(MapContext);

// --- Map Component ---
export const Map = forwardRef(({ 
  className, 
  center = [90.4125, 23.8103], 
  zoom = 12, 
  style = {}, 
  children,
  onLoad,
  onClick,
  ...props 
}, ref) => {
  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Expose map instance via ref
  useImperativeHandle(ref, () => ({
    getMap: () => mapInstance.current,
    flyTo: (args) => mapInstance.current?.flyTo(args),
  }));

  const MAP_STYLE_URL = {
    "version": 8,
    "sources": {
      "osm": {
        "type": "raster",
        "tiles": ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
        "tileSize": 256,
        "attribution": "&copy; OpenStreetMap Contributors",
        "maxzoom": 19
      }
    },
    "layers": [
      {
        "id": "osm",
        "type": "raster",
        "source": "osm"
      }
    ]
  };

  useEffect(() => {
    if (mapInstance.current) return;
    if (!mapContainer.current) return;

    try {
        const map = new maplibregl.Map({
          container: mapContainer.current,
          style: MAP_STYLE_URL,
          center: center,
          zoom: zoom,
          attributionControl: false,
        });

        mapInstance.current = map;

        map.on('load', () => {
          setIsLoaded(true);
          map.resize();
          if (onLoad) onLoad(map);
        });

        if (onClick) {
            map.on('click', (e) => {
                onClick({ lat: e.lngLat.lat, lng: e.lngLat.lng });
            });
        }

        return () => {
          map.remove();
          mapInstance.current = null;
        };
    } catch (err) {
        console.error("Map init failed", err);
    }
  }, []);

  useEffect(() => {
      const observer = new ResizeObserver(() => {
          if (mapInstance.current) mapInstance.current.resize();
      });
      if (mapContainer.current) observer.observe(mapContainer.current);
      return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (mapInstance.current && center) {
        mapInstance.current.setCenter(center);
    }
  }, [center]);

  return (
    <MapContext.Provider value={{ map: mapInstance.current, isLoaded }}>
        <div 
            ref={mapContainer} 
            className={cn("relative w-full h-full rounded-lg overflow-hidden bg-slate-100", className)} 
            style={{ minHeight: '300px', ...style }} 
            {...props}
        >
            {isLoaded && children}
        </div>
    </MapContext.Provider>
  );
});

// --- Map Controls ---
export const MapControls = ({ position = 'bottom-right', showZoom = true, showLocate = true, showCompass = false, onGeolocate }) => {
    const { map } = useMap();

    useEffect(() => {
        if (!map) return;

        const nav = new maplibregl.NavigationControl({
            showCompass: showCompass,
            showZoom: showZoom
        });

        const geolocate = new maplibregl.GeolocateControl({
            positionOptions: { enableHighAccuracy: true },
            trackUserLocation: true,
            showUserLocation: true
        });

        if (onGeolocate) {
            geolocate.on('geolocate', (e) => {
                if (e && e.coords) {
                    onGeolocate({
                        lng: e.coords.longitude,
                        lat: e.coords.latitude
                    });
                }
            });
        }

        if (showZoom || showCompass) map.addControl(nav, position);
        if (showLocate) map.addControl(geolocate, position);

        return () => {
            if (map) {
                try {
                    if (showZoom || showCompass) map.removeControl(nav);
                    if (showLocate) map.removeControl(geolocate);
                } catch (e) {}
            }
        };
    }, [map, position, showZoom, showLocate, showCompass]);

    return null;
};

// --- Map Marker ---
export const MapMarker = ({ longitude, latitude, draggable = false, onDragEnd, children, onClick }) => {
    const { map } = useMap();
    const markerRef = useRef(null);
    const elementRef = useRef(document.createElement('div'));

    useEffect(() => {
        if (!map) return;
        if (typeof longitude !== 'number' || typeof latitude !== 'number' || isNaN(longitude) || isNaN(latitude)) return;

        const el = elementRef.current;
        if (!children) {
            el.className = 'w-6 h-6 bg-red-500 rounded-full border-2 border-white shadow-lg cursor-pointer transform hover:scale-110 transition-transform';
        }

        const marker = new maplibregl.Marker({
            element: el,
            draggable: draggable
        })
        .setLngLat([longitude, latitude])
        .addTo(map);

        markerRef.current = marker;

        if (onDragEnd) {
            marker.on('dragend', () => {
                const lngLat = marker.getLngLat();
                onDragEnd({ lng: lngLat.lng, lat: lngLat.lat });
            });
        }

        if (onClick) {
            el.onclick = onClick;
        }

        return () => {
            marker.remove();
        };
    }, [map]); 

    useEffect(() => {
        if (markerRef.current && typeof longitude === 'number' && typeof latitude === 'number' && !isNaN(longitude) && !isNaN(latitude)) {
            markerRef.current.setLngLat([longitude, latitude]);
        }
    }, [longitude, latitude]);

    return children ? createPortal(children, elementRef.current) : null; 
};

// --- Marker Content ---
export const MarkerContent = ({ children, className }) => (
    <div className={cn("cursor-pointer", className)}>{children}</div>
);

// --- Marker Label ---
export const MarkerLabel = ({ children, position = 'bottom', className }) => {
    const posClasses = {
        top: 'bottom-full mb-2',
        bottom: 'top-full mt-2',
        left: 'right-full mr-2',
        right: 'left-full ml-2'
    };
    return (
        <div className={cn(
            "absolute left-1/2 -translate-x-1/2 whitespace-nowrap bg-white/90 backdrop-blur px-2 py-1 rounded shadow-sm text-[10px] font-bold text-slate-700 border border-slate-100 pointer-events-none",
            posClasses[position],
            className
        )}>
            {children}
        </div>
    );
};

// --- Map Route ---
export const MapRoute = ({ coordinates, color = '#3b82f6', width = 4, opacity = 0.8 }) => {
    const { map, isLoaded } = useMap();
    const layerId = useRef(`route-${Math.random().toString(36).substr(2, 9)}`);

    useEffect(() => {
        if (!map || !isLoaded || !coordinates || coordinates.length === 0) return;

        const id = layerId.current;
        
        map.addSource(id, {
            'type': 'geojson',
            'data': {
                'type': 'Feature',
                'properties': {},
                'geometry': {
                    'type': 'LineString',
                    'coordinates': coordinates
                }
            }
        });

        map.addLayer({
            'id': id,
            'type': 'line',
            'source': id,
            'layout': {
                'line-join': 'round',
                'line-cap': 'round'
            },
            'paint': {
                'line-color': color,
                'line-width': width,
                'line-opacity': opacity
            }
        });

        return () => {
            if (map.getLayer(id)) map.removeLayer(id);
            if (map.getSource(id)) map.removeSource(id);
        };
    }, [map, isLoaded, coordinates, color, width, opacity]);

    return null;
};